import { buildI18n, pickT } from "@repo/shared";
import type {
  CollectionSustainabilityConfig,
  LocalizedString,
  SfdrScoreValue,
  SustainabilityAlignmentMode,
  SustainabilityExclusionKey,
  SustainabilityExclusionMode,
  SustainabilityProductPreferenceCriterion,
  SustainabilityProductPreferencesDisplay,
  SustainabilityScaleCriterion,
  SustainabilityScaleStep,
  SustainabilitySfdrScore,
} from "@repo/shared/types";
import {
  allowedAlignmentModes,
  DEFAULT_SUSTAINABILITY_EXCLUSION_MODE,
  effectiveProductPreferencesDisplay,
  SUSTAINABILITY_MAX_STEPS,
  SUSTAINABILITY_MIN_STEPS,
  SUSTAINABILITY_SHORT_LABEL_MAX_LENGTH,
} from "@repo/shared/types";
import { ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { AlertMessage } from "@/components/ui/alert-message";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OptionRow } from "@/components/ui/option-row";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SvgIconField } from "@/components/ui/svg-icon-field";
import { Switch } from "@/components/ui/switch";
import { TranslationButton } from "@/components/ui/translation-button";
import { useUpdateSustainabilityConfig } from "@/lib/hooks/use-portfolio-collections";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { EXCLUSION_CATALOG, EXCLUSION_KEYS } from "./exclusion-catalog";
import { DEFAULT_SFDR_SCORES } from "./sfdr-defaults";
import { PRODUCT_PREFERENCE_LABEL_SUFFIX, T } from "./sustainability-config-editor.i18n";

type StepRow = {
  /** Stable local identity for React keys. */
  uid: string;
  /** Internal stable identifier (auto-generated UUID). */
  key: string;
  labelI18n: LocalizedString;
};

type ScaleRow = {
  uid: string;
  key: string;
  labelI18n: LocalizedString;
  /** QL-414 — short name for table headers; empty = the label is used. */
  shortLabelI18n: LocalizedString;
  steps: StepRow[];
};

type ProductPreferenceRow = {
  uid: string;
  key: string;
  labelI18n: LocalizedString;
  iconSvg: string;
  exclusionKey: SustainabilityExclusionKey | null;
  exclusionMode: SustainabilityExclusionMode;
};

type SfdrScoreRow = {
  value: SfdrScoreValue;
  labelI18n: LocalizedString;
};

const toStepRows = (steps: SustainabilityScaleStep[] | undefined): StepRow[] =>
  (steps ?? []).map((s) => ({ uid: crypto.randomUUID(), key: s.key, labelI18n: s.labelI18n }));

/** Merge stored SFDR scores onto the three fixed 6/8/9 rows (defaults fill gaps). */
const toSfdrScoreRows = (scores: SustainabilitySfdrScore[] | undefined): SfdrScoreRow[] =>
  DEFAULT_SFDR_SCORES.map((def) => {
    const stored = (scores ?? []).find((s) => s.value === def.value);
    return { value: def.value, labelI18n: stored?.labelI18n ?? def.labelI18n };
  });

type SustainabilityConfigEditorProps = {
  portfolioCollectionId: string;
  config: CollectionSustainabilityConfig | null;
};

export function SustainabilityConfigEditor({
  portfolioCollectionId,
  config,
}: SustainabilityConfigEditorProps) {
  const { userLanguage, tenantSupportedLanguages } = useUserLanguage();
  const t = useT(T);
  const updateConfig = useUpdateSustainabilityConfig();

  const [sfdrEnabled, setSfdrEnabled] = useState(config?.sfdr != null);
  const [sfdrLabel, setSfdrLabel] = useState<LocalizedString>(config?.sfdr?.labelI18n ?? {});
  const [sfdrScores, setSfdrScores] = useState<SfdrScoreRow[]>(
    toSfdrScoreRows(config?.sfdr?.scores),
  );
  const [bars, setBars] = useState<ScaleRow[]>(
    (config?.bars ?? []).map((b) => ({
      uid: crypto.randomUUID(),
      key: b.key,
      labelI18n: b.labelI18n,
      shortLabelI18n: b.shortLabelI18n ?? {},
      steps: toStepRows(b.steps),
    })),
  );
  const [productPreferences, setProductPreferences] = useState<ProductPreferenceRow[]>(() =>
    (config?.productPreferences ?? []).map((pref) => ({
      uid: crypto.randomUUID(),
      key: pref.key,
      labelI18n: pref.labelI18n,
      iconSvg: pref.iconSvg ?? "",
      exclusionKey: pref.exclusionKey ?? null,
      exclusionMode: pref.exclusionMode ?? DEFAULT_SUSTAINABILITY_EXCLUSION_MODE,
    })),
  );
  // "client" = both modes allowed. Seeded through the resolver so a config with
  // no alignmentModes and one listing both land on the same option.
  const [alignmentChoice, setAlignmentChoice] = useState<SustainabilityAlignmentMode | "client">(
    () => {
      const allowed = allowedAlignmentModes(config);
      return allowed.length === 1 ? allowed[0]! : "client";
    },
  );
  const [noPreferenceEnabled, setNoPreferenceEnabled] = useState(
    config?.productPreferencesNoPreferenceEnabled === true,
  );
  const [display, setDisplay] = useState<SustainabilityProductPreferencesDisplay>(() =>
    effectiveProductPreferencesDisplay(config),
  );
  const [formError, setFormError] = useState<string | null>(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState<{
    title: string;
    description: string;
    variant: "default" | "destructive";
  }>({ title: "", description: "", variant: "default" });

  const getLabel = (labelI18n: LocalizedString): string =>
    labelI18n[userLanguage] ?? Object.values(labelI18n)[0] ?? "";

  const setLabelValue = (labelI18n: LocalizedString, value: string): LocalizedString =>
    Object.keys(labelI18n).length > 0
      ? { ...labelI18n, [userLanguage]: value }
      : buildI18n(value, userLanguage, tenantSupportedLanguages);

  // QL-414 — the short label is optional and per language: a header uses it
  // only in the language it was written in, else the full question. So unlike
  // setLabelValue it is never fanned out to every tenant language, the input
  // shows the user's language only, and emptying it removes that language's
  // text and leaves the translations alone.
  const getShortLabel = (shortLabelI18n: LocalizedString): string =>
    shortLabelI18n[userLanguage] ?? "";

  const setShortLabelValue = (shortLabelI18n: LocalizedString, value: string): LocalizedString => {
    const { [userLanguage]: _previous, ...others } = shortLabelI18n;
    return value === "" ? others : { ...others, [userLanguage]: value };
  };

  const newStep = (): StepRow => ({
    uid: crypto.randomUUID(),
    key: crypto.randomUUID(),
    labelI18n: {},
  });

  const validate = (): CollectionSustainabilityConfig | false => {
    const collectSteps = (rows: StepRow[]): SustainabilityScaleStep[] | false => {
      if (rows.length < SUSTAINABILITY_MIN_STEPS) {
        setFormError(t.minStepsError);
        return false;
      }
      const result: SustainabilityScaleStep[] = [];
      for (const row of rows) {
        if (!getLabel(row.labelI18n).trim()) {
          setFormError(t.stepLabelRequired);
          return false;
        }
        result.push({ key: row.key, labelI18n: row.labelI18n });
      }
      return result;
    };

    if (sfdrEnabled && !getLabel(sfdrLabel).trim()) {
      setFormError(t.labelRequired);
      return false;
    }
    let sfdr: CollectionSustainabilityConfig["sfdr"] = null;
    if (sfdrEnabled) {
      const scores: SustainabilitySfdrScore[] = [];
      for (const row of sfdrScores) {
        if (!getLabel(row.labelI18n).trim()) {
          setFormError(t.sfdrScoreLabelRequired);
          return false;
        }
        scores.push({ value: row.value, labelI18n: row.labelI18n });
      }
      sfdr = { labelI18n: sfdrLabel, scores };
    }

    const validBars: SustainabilityScaleCriterion[] = [];
    for (const bar of bars) {
      if (!getLabel(bar.labelI18n).trim()) {
        setFormError(t.labelRequired);
        return false;
      }
      // The input caps what is typed; a translation can still be too long.
      const tooLong = Object.entries(bar.shortLabelI18n).find(
        ([, text]) => (text ?? "").trim().length > SUSTAINABILITY_SHORT_LABEL_MAX_LENGTH,
      );
      if (tooLong) {
        setFormError(t.shortLabelTooLong(getLabel(bar.labelI18n), tooLong[0]));
        return false;
      }
      const steps = collectSteps(bar.steps);
      if (steps === false) return false;
      validBars.push({
        key: bar.key,
        labelI18n: bar.labelI18n,
        shortLabelI18n: bar.shortLabelI18n,
        steps,
      });
    }

    const validProductPreferences: SustainabilityProductPreferenceCriterion[] = [];
    for (const pref of productPreferences) {
      if (!getLabel(pref.labelI18n).trim()) {
        setFormError(t.labelRequired);
        return false;
      }
      validProductPreferences.push({
        key: pref.key,
        labelI18n: pref.labelI18n,
        ...(pref.iconSvg.trim() ? { iconSvg: pref.iconSvg.trim() } : {}),
        ...(pref.exclusionKey
          ? { exclusionKey: pref.exclusionKey, exclusionMode: pref.exclusionMode }
          : {}),
      });
    }

    return {
      sfdr,
      bars: validBars,
      productPreferences: validProductPreferences,
      // Three options make the empty-list-as-"no mode" state unrepresentable.
      alignmentModes: alignmentChoice === "client" ? [] : [alignmentChoice],
      productPreferencesNoPreferenceEnabled: noPreferenceEnabled,
      productPreferencesDisplay: display,
    };
  };

  const handleSave = () => {
    setFormError(null);
    const nextConfig = validate();
    if (nextConfig === false) return;
    updateConfig.mutate(
      { collectionId: portfolioCollectionId, config: nextConfig },
      {
        onSuccess: () => {
          setAlertConfig({ title: t.saved, description: t.savedDescription, variant: "default" });
          setAlertOpen(true);
        },
        onError: (err: Error) => {
          setAlertConfig({
            title: t.error,
            description: err.message || t.failedToSave,
            variant: "destructive",
          });
          setAlertOpen(true);
        },
      },
    );
  };

  /** Ordered steps sub-editor: label + TranslationButton, reorder up/down, remove, add. */
  const renderStepsEditor = (steps: StepRow[], setSteps: (rows: StepRow[]) => void) => (
    <div className="space-y-2 pl-1">
      <div>
        <Label className="text-sm">{t.stepsLabel}</Label>
        <p className="text-xs text-muted-foreground">{t.stepsHint}</p>
      </div>
      {steps.map((row, index) => (
        <div key={row.uid} className="flex items-center gap-2">
          <span className="w-5 text-xs text-muted-foreground text-right">{index + 1}.</span>
          <Input
            className="flex-1"
            placeholder={t.stepPlaceholder}
            value={getLabel(row.labelI18n)}
            onChange={(e) =>
              setSteps(
                steps.map((r, i) =>
                  i === index ? { ...r, labelI18n: setLabelValue(r.labelI18n, e.target.value) } : r,
                ),
              )
            }
            disabled={updateConfig.isPending}
          />
          <TranslationButton
            value={row.labelI18n}
            onSave={(labelI18n) =>
              setSteps(steps.map((r, i) => (i === index ? { ...r, labelI18n } : r)))
            }
            supportedLanguages={tenantSupportedLanguages}
            disabled={updateConfig.isPending}
          />
          <Button
            variant="ghost"
            size="icon"
            tooltip={t.moveUp}
            onClick={() => {
              if (index === 0) return;
              const next = [...steps];
              [next[index - 1], next[index]] = [next[index]!, next[index - 1]!];
              setSteps(next);
            }}
            disabled={index === 0 || updateConfig.isPending}
          >
            <ChevronUp className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            tooltip={t.moveDown}
            onClick={() => {
              if (index === steps.length - 1) return;
              const next = [...steps];
              [next[index], next[index + 1]] = [next[index + 1]!, next[index]!];
              setSteps(next);
            }}
            disabled={index === steps.length - 1 || updateConfig.isPending}
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            tooltip={t.remove}
            onClick={() => setSteps(steps.filter((_, i) => i !== index))}
            disabled={steps.length <= SUSTAINABILITY_MIN_STEPS || updateConfig.isPending}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setSteps([...steps, newStep()])}
        disabled={steps.length >= SUSTAINABILITY_MAX_STEPS || updateConfig.isPending}
      >
        <Plus className="h-4 w-4 mr-1" />
        {t.addStep}
      </Button>
    </div>
  );

  /** Map a product preference to an exclusion: pre-fill its label + icon from the catalog (both editable). */
  const applyExclusion = (index: number, exclusionKey: SustainabilityExclusionKey | null) => {
    setProductPreferences((curr) =>
      curr.map((r, i) => {
        if (i !== index) return r;
        if (!exclusionKey) return { ...r, exclusionKey: null };
        const entry = EXCLUSION_CATALOG[exclusionKey];
        // Pre-fill label only when empty, and icon only when unset — don't clobber edits.
        // The product preference label is the client-facing preference, so phrase it as
        // such ("Pesticides exclusion / no involvement"); the bare category name stays on
        // the catalog for the category dropdown + the diagnostic tab.
        const labelI18n = getLabel(r.labelI18n).trim()
          ? r.labelI18n
          : Object.fromEntries(
              Object.entries(entry.defaultLabelI18n).map(([lang, name]) => [
                lang,
                `${name}${pickT(PRODUCT_PREFERENCE_LABEL_SUFFIX, lang).suffix}`,
              ]),
            );
        const iconSvg = r.iconSvg.trim() ? r.iconSvg : entry.defaultIconSvg;
        // Keep the existing mode when re-picking a sector; default to "either".
        const exclusionMode = r.exclusionMode ?? DEFAULT_SUSTAINABILITY_EXCLUSION_MODE;
        return { ...r, exclusionKey, labelI18n, iconSvg, exclusionMode };
      }),
    );
  };

  const renderProductPreferenceRows = () => (
    <div className="space-y-3">
      {productPreferences.map((row, index) => (
        <div key={row.uid} className="space-y-2 border rounded-md p-3">
          <div className="flex items-center gap-2">
            <Input
              className="flex-1"
              placeholder={t.labelPlaceholder}
              value={getLabel(row.labelI18n)}
              onChange={(e) =>
                setProductPreferences((curr) =>
                  curr.map((r, i) =>
                    i === index
                      ? { ...r, labelI18n: setLabelValue(r.labelI18n, e.target.value) }
                      : r,
                  ),
                )
              }
              disabled={updateConfig.isPending}
            />
            <TranslationButton
              value={row.labelI18n}
              onSave={(labelI18n) =>
                setProductPreferences((curr) =>
                  curr.map((r, i) => (i === index ? { ...r, labelI18n } : r)),
                )
              }
              supportedLanguages={tenantSupportedLanguages}
              disabled={updateConfig.isPending}
            />
            <Button
              variant="ghost"
              size="icon"
              tooltip={t.remove}
              onClick={() => setProductPreferences((curr) => curr.filter((_, i) => i !== index))}
              disabled={updateConfig.isPending}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">
                {t.productPreferenceExclusionLabel}
              </Label>
              <Select
                value={row.exclusionKey ?? "__none__"}
                onValueChange={(v) =>
                  applyExclusion(index, v === "__none__" ? null : (v as SustainabilityExclusionKey))
                }
                disabled={updateConfig.isPending}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t.productPreferenceExclusionNone}</SelectItem>
                  {EXCLUSION_KEYS.map((key) => (
                    <SelectItem key={key} value={key}>
                      {getLabel(EXCLUSION_CATALOG[key].defaultLabelI18n)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {row.exclusionKey && (
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">
                  {t.productPreferenceModeLabel}
                </Label>
                <Select
                  value={row.exclusionMode}
                  onValueChange={(v) =>
                    setProductPreferences((curr) =>
                      curr.map((r, i) =>
                        i === index ? { ...r, exclusionMode: v as SustainabilityExclusionMode } : r,
                      ),
                    )
                  }
                  disabled={updateConfig.isPending}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="excludes">{t.productPreferenceModeExcludes}</SelectItem>
                    <SelectItem value="not_involved">
                      {t.productPreferenceModeNotInvolved}
                    </SelectItem>
                    <SelectItem value="either">{t.productPreferenceModeEither}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">
                {t.productPreferenceIconLabel}
              </Label>
              <SvgIconField
                value={row.iconSvg}
                onChange={(iconSvg) =>
                  setProductPreferences((curr) =>
                    curr.map((r, i) => (i === index ? { ...r, iconSvg } : r)),
                  )
                }
                disabled={updateConfig.isPending}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-6 max-w-3xl">
      <p className="text-sm text-muted-foreground">{t.description}</p>

      <div className="space-y-3 border rounded-lg p-4">
        <Label className="text-base">{t.sfdrSection}</Label>
        <div className="flex items-center gap-2">
          <Switch
            id="sfdr-enabled"
            checked={sfdrEnabled}
            onCheckedChange={setSfdrEnabled}
            disabled={updateConfig.isPending}
          />
          <Label htmlFor="sfdr-enabled" className="font-normal">
            {t.sfdrUse}
          </Label>
        </div>
        {sfdrEnabled && (
          <>
            <div className="space-y-1">
              <Label className="text-sm">{t.sfdrQuestionLabel}</Label>
              <p className="text-xs text-muted-foreground">{t.sfdrQuestionHint}</p>
            </div>
            <div className="flex items-center gap-2">
              <Input
                className="flex-1"
                placeholder={t.sfdrQuestionPlaceholder}
                value={getLabel(sfdrLabel)}
                onChange={(e) => setSfdrLabel(setLabelValue(sfdrLabel, e.target.value))}
                disabled={updateConfig.isPending}
              />
              <TranslationButton
                value={sfdrLabel}
                onSave={setSfdrLabel}
                supportedLanguages={tenantSupportedLanguages}
                disabled={updateConfig.isPending}
              />
            </div>
            <div className="space-y-2 pl-1">
              <div>
                <Label className="text-sm">{t.sfdrScoresLabel}</Label>
                <p className="text-xs text-muted-foreground">{t.sfdrScoresHint}</p>
              </div>
              {sfdrScores.map((row, index) => (
                <div key={row.value} className="flex items-center gap-2">
                  <span className="w-20 text-xs text-muted-foreground">
                    {t.sfdrArticle} {row.value}
                  </span>
                  <Input
                    className="flex-1"
                    value={getLabel(row.labelI18n)}
                    onChange={(e) =>
                      setSfdrScores(
                        sfdrScores.map((r, i) =>
                          i === index
                            ? { ...r, labelI18n: setLabelValue(r.labelI18n, e.target.value) }
                            : r,
                        ),
                      )
                    }
                    disabled={updateConfig.isPending}
                  />
                  <TranslationButton
                    value={row.labelI18n}
                    onSave={(labelI18n) =>
                      setSfdrScores(
                        sfdrScores.map((r, i) => (i === index ? { ...r, labelI18n } : r)),
                      )
                    }
                    supportedLanguages={tenantSupportedLanguages}
                    disabled={updateConfig.isPending}
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="space-y-3 border rounded-lg p-4">
        <div>
          <Label className="text-base">{t.barsSection}</Label>
          <p className="text-xs text-muted-foreground">{t.barsDescription}</p>
        </div>
        <div className="space-y-4">
          {bars.map((bar, index) => (
            <div key={bar.uid} className="space-y-2 border rounded-md p-3">
              <div className="flex items-center gap-2">
                <Input
                  className="flex-1"
                  placeholder={t.labelPlaceholder}
                  value={getLabel(bar.labelI18n)}
                  onChange={(e) =>
                    setBars(
                      bars.map((r, i) =>
                        i === index
                          ? { ...r, labelI18n: setLabelValue(r.labelI18n, e.target.value) }
                          : r,
                      ),
                    )
                  }
                  disabled={updateConfig.isPending}
                />
                <TranslationButton
                  value={bar.labelI18n}
                  onSave={(labelI18n) =>
                    setBars(bars.map((r, i) => (i === index ? { ...r, labelI18n } : r)))
                  }
                  supportedLanguages={tenantSupportedLanguages}
                  disabled={updateConfig.isPending}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  tooltip={t.remove}
                  onClick={() => setBars(bars.filter((_, i) => i !== index))}
                  disabled={updateConfig.isPending}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="space-y-1">
                <Label htmlFor={`bar-short-label-${bar.uid}`} className="text-sm">
                  {t.shortLabel}
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    id={`bar-short-label-${bar.uid}`}
                    className="flex-1"
                    placeholder={t.shortLabelPlaceholder}
                    maxLength={SUSTAINABILITY_SHORT_LABEL_MAX_LENGTH}
                    value={getShortLabel(bar.shortLabelI18n)}
                    onChange={(e) =>
                      setBars(
                        bars.map((r, i) =>
                          i === index
                            ? {
                                ...r,
                                shortLabelI18n: setShortLabelValue(
                                  r.shortLabelI18n,
                                  e.target.value,
                                ),
                              }
                            : r,
                        ),
                      )
                    }
                    disabled={updateConfig.isPending}
                  />
                  <TranslationButton
                    value={bar.shortLabelI18n}
                    onSave={(shortLabelI18n) =>
                      setBars(bars.map((r, i) => (i === index ? { ...r, shortLabelI18n } : r)))
                    }
                    supportedLanguages={tenantSupportedLanguages}
                    disabled={updateConfig.isPending}
                  />
                </div>
                <p className="text-xs text-muted-foreground">{t.shortLabelHelp}</p>
              </div>
              {renderStepsEditor(bar.steps, (steps) =>
                setBars(bars.map((r, i) => (i === index ? { ...r, steps } : r))),
              )}
            </div>
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            setBars([
              ...bars,
              {
                uid: crypto.randomUUID(),
                key: crypto.randomUUID(),
                labelI18n: {},
                shortLabelI18n: {},
                steps: [newStep(), newStep()],
              },
            ])
          }
          disabled={bars.length >= 2 || updateConfig.isPending}
        >
          <Plus className="h-4 w-4 mr-1" />
          {t.addBar}
        </Button>
      </div>

      <div className="space-y-3 border rounded-lg p-4">
        <div>
          <Label className="text-base">{t.productPreferencesSection}</Label>
          <p className="text-xs text-muted-foreground">{t.productPreferencesDescription}</p>
        </div>
        <div className="flex items-start gap-2">
          <Switch
            id="product-preferences-no-preference"
            checked={noPreferenceEnabled}
            onCheckedChange={setNoPreferenceEnabled}
            disabled={updateConfig.isPending}
          />
          <div className="space-y-0.5">
            <Label htmlFor="product-preferences-no-preference" className="font-normal">
              {t.noPreferenceToggle}
            </Label>
            <p className="text-xs text-muted-foreground">{t.noPreferenceToggleHint}</p>
          </div>
        </div>
        <div className="space-y-1 sm:max-w-xs">
          <Label htmlFor="product-preferences-display" className="text-sm">
            {t.displayLabel}
          </Label>
          <p className="text-xs text-muted-foreground">{t.displayHint}</p>
          <Select
            value={display}
            onValueChange={(v) => setDisplay(v as SustainabilityProductPreferencesDisplay)}
            disabled={updateConfig.isPending}
          >
            <SelectTrigger id="product-preferences-display" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="checkbox">{t.displayCheckbox}</SelectItem>
              <SelectItem value="card">{t.displayCards}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {renderProductPreferenceRows()}
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            setProductPreferences((curr) => [
              ...curr,
              {
                uid: crypto.randomUUID(),
                key: crypto.randomUUID(),
                labelI18n: {},
                iconSvg: "",
                exclusionKey: null,
                exclusionMode: DEFAULT_SUSTAINABILITY_EXCLUSION_MODE,
              },
            ])
          }
          disabled={updateConfig.isPending}
        >
          <Plus className="h-4 w-4 mr-1" />
          {t.addProductPreference}
        </Button>
      </div>

      <div className="space-y-3 border rounded-lg p-4">
        <div>
          <Label className="text-base">{t.alignmentSection}</Label>
          <p className="text-xs text-muted-foreground">{t.alignmentDescription}</p>
        </div>
        <RadioGroup
          value={alignmentChoice}
          onValueChange={(value) =>
            setAlignmentChoice(value as SustainabilityAlignmentMode | "client")
          }
          disabled={updateConfig.isPending}
        >
          {(
            [
              ["individual", t.alignmentIndividual, t.alignmentIndividualHint],
              ["portfolio", t.alignmentPortfolio, t.alignmentPortfolioHint],
              ["client", t.alignmentClient, t.alignmentClientHint],
            ] as const
          ).map(([value, title, hint]) => (
            <OptionRow key={value} align="start">
              <RadioGroupItem value={value} id={`alignment-${value}`} className="mt-0.5" />
              <Label
                htmlFor={`alignment-${value}`}
                className="font-normal cursor-pointer leading-snug"
              >
                {title}
                <span className="block text-xs text-muted-foreground">{hint}</span>
              </Label>
            </OptionRow>
          ))}
        </RadioGroup>
      </div>

      {formError && <p className="text-sm text-destructive">{formError}</p>}

      <Button onClick={handleSave} loading={updateConfig.isPending}>
        {t.save}
      </Button>

      <AlertMessage
        open={alertOpen}
        onOpenChange={setAlertOpen}
        title={alertConfig.title}
        description={alertConfig.description}
        variant={alertConfig.variant}
      />
    </div>
  );
}
